import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type FormStep = 'one' | 'two' | 'three' | 'none';

@Injectable({
  providedIn: 'root',
})
export class FormModalService {
  private currentStepSubject = new BehaviorSubject<FormStep>('none');
  currentStep$ = this.currentStepSubject.asObservable();

  private formDataSubject = new BehaviorSubject<any>({});
  formData$ = this.formDataSubject.asObservable();

  private serviceTypeSubject = new BehaviorSubject<string>('');
  serviceType$ = this.serviceTypeSubject.asObservable();

  private formCompletedSubject = new BehaviorSubject<boolean>(false);
  formCompleted$ = this.formCompletedSubject.asObservable();

  constructor() {
    // Charger les données sauvegardées au démarrage du service
    const savedData = localStorage.getItem('formData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        this.formDataSubject.next(parsedData);
      } catch (e) {
        console.error('Erreur lors du chargement des données du formulaire:', e);
      }
    }
  }

  openForm(serviceType: string) {
    this.serviceTypeSubject.next(serviceType);
    this.currentStepSubject.next('one');
    this.formCompletedSubject.next(false);
    document.body.classList.add('modal-open');
  }

  closeForm() {
    // Vérifier si le formulaire est terminé ou si l'utilisateur est sur la première étape
    if (
      this.formCompletedSubject.value ||
      this.currentStepSubject.value === 'one'
    ) {
      this.currentStepSubject.next('none');
      document.body.classList.remove('modal-open');
    } else {
      // Au lieu d'utiliser confirm(), émettre un état de confirmation
      this.showConfirmationSubject.next(true);
    }
  }

  // Ajouter ces propriétés et méthodes
  private showConfirmationSubject = new BehaviorSubject<boolean>(false);
  showConfirmation$ = this.showConfirmationSubject.asObservable();

  confirmClose() {
    this.showConfirmationSubject.next(false);
    this.currentStepSubject.next('none');
    document.body.classList.remove('modal-open');
    
    // Option: Conserver les données pour une reprise ultérieure
    // Si vous voulez effacer les données à la fermeture, décommentez la ligne suivante:
    // localStorage.removeItem('formData');
  }

  cancelClose() {
    this.showConfirmationSubject.next(false);
  }

  completeForm() {
    this.formCompletedSubject.next(true);
    // Nettoyer les données du formulaire après soumission réussie
    localStorage.removeItem('formData');
    this.formDataSubject.next({});
  }

  nextStep(currentStep: FormStep, formData: any) {
    // Fusionner les données existantes avec les nouvelles données
    const updatedData = { ...this.formDataSubject.value, ...formData };
    this.formDataSubject.next(updatedData);
    
    // Sauvegarder dans le localStorage pour persistance
    localStorage.setItem('formData', JSON.stringify(updatedData));

    // Mettre à jour directement le currentStep avec la valeur reçue
    this.currentStepSubject.next(currentStep);
  }

  previousStep(currentStep: FormStep) {
    // Mettre à jour directement le currentStep avec la valeur reçue
    this.currentStepSubject.next(currentStep);
  }

  getFormData() {
    return this.formDataSubject.value;
  }
}